import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ContextMenuTrigger } from "rctx-contextmenu";
import { updateMessageOptions } from "../../../store/Slices/MessageOptionsSlice";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { updateCreateGroupData } from "../../../store/Slices/CreateGroupSlice";
import { TbUserPlus } from "react-icons/tb";
import { updateSelectedGroupMember } from "../../../store/Slices/SelectedGroupMemberSlice";
import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import useApiPost from "../../../hooks/PostData";
import toast from "react-hot-toast";
import TextTranslate from "../../../utils/TextTranslate";
import { useConversationMembers } from "../../../store/api/useConversationMembers";
import { GroupMemberRecord } from "../../../types/ResType";
import ShowSavedName from "../../../components/ShowSavedName";
import { ClipLoader } from "react-spinners";
import { useTheme } from "../../../context/ThemeProvider";
import { useNavigateToChat } from "../../../hooks/useNavigateToChat";

export default function GroupMembers() {
  const {
    data: GroupMemberData,
    isLoading: GroupMemberDataLoading,
    refetch,
  } = useConversationMembers();
  const OnlineUserList = useAppSelector((state) => state.OnlineUserList);
  const userData = useAppSelector((state) => state.userData);
  const dispatch = useAppDispatch();
  const { postData } = useApiPost();
  // @ts-ignore
  const { theme } = useTheme();
  const selectedGroupMember = useAppSelector(
    (state) => state.SelectedGroupMember,
  );
  const navigateToChat = useNavigateToChat();
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const ChatListArray = useAppSelector((state) => state.chatList);

  const isCurrentUserAdmin = !!GroupMemberData?.data.Records.find(
    (convoUser) => convoUser.User.user_id === userData.user_id,
  )?.is_admin;

  async function makeGrouAdmin() {
    await postData("chat/create-group-admin", {
      admin_user_id: selectedGroupMember.user_id,
      chat_id: currentConversationData.chat_id.toString(),
    });
    refetch();
    toast.success(
      `${selectedGroupMember.User.full_name} ${selectedGroupMember.is_admin ? "removed from" : "is now"} group admin`,
    );
  }

  function changeCurrentConversation(member: GroupMemberRecord) {
    if (member.User.user_id == userData.user_id) {
      return;
    }
    const chatData = ChatListArray.Chats.find(
      (chatUser) =>
        chatUser.PeerUserData.user_id == member.User.user_id &&
        chatUser.Records[0].chat_type == "private",
    )?.Records[0];

    navigateToChat({
      chat_id: chatData?.chat_id ?? -1,
      chat_type: "private",
      full_name: member.User.full_name,
      group_name: "",
      group_icon: "",
      profile_pic: member.User.profile_pic,
      user_id: member.User.user_id,
      user_name: member.User.user_name,
      email_id: member.User.email,
      unseen_count: 0,
      is_group_blocked: chatData?.blocked_by?.includes(
        userData.user_id.toString(),
      ),
      other_user_blocked_you:
        chatData?.blocked_by?.length &&
        chatData?.blocked_by.length >= 1 &&
        !chatData?.blocked_by.includes(userData.user_id.toString())
          ? true
          : false,
    });
  }

  if (currentConversationData.chat_type != "group") return null;

  return (
    <div className="space-y-1 rounded-2xl bg-primary px-3 py-3 shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
      <div className="flex justify-between px-1.5 pb-1">
        <div className="text-[13px] font-medium uppercase tracking-wide text-lightText">
          <TextTranslate text="Group Members" />
        </div>
        <div className="text-[13px] tabular-nums text-lightText">
          {GroupMemberData?.data.Records.length}
        </div>
      </div>

      {isCurrentUserAdmin && (
        <button
          type="button"
          onClick={() => {
            const existing_member_user_id = GroupMemberData?.data.Records.map(
              (user) => user.User.user_id,
            );
            dispatch(
              updateCreateGroupData({
                show_add_member_modal: true,
                existing_member_user_id: existing_member_user_id,
              }),
            );
          }}
          className="flex w-full cursor-pointer items-center rounded-xl px-1.5 py-2 text-left active:bg-black/[0.04] dark:active:bg-white/[0.06]"
        >
          <div className="relative mr-3 grid h-10 w-10 shrink-0 place-content-center rounded-full primary-gradient">
            <TbUserPlus className="h-5 w-5" />
          </div>
          <div className="text-base font-medium capitalize text-darkText">
            <TextTranslate text="Add Member" />
          </div>
        </button>
      )}

      {GroupMemberDataLoading ? (
        <div className="flex h-60 items-center justify-center">
          <ClipLoader
            size={19}
            color={theme == "dark" ? "white" : "black"}
          />
        </div>
      ) : (
        GroupMemberData?.data.Records.map((member) => (
          <ContextMenuTrigger
            id="my-context-menu-2"
            key={member.User.user_id}
          >
            <div
              onContextMenu={() => {
                dispatch(updateSelectedGroupMember(member));
              }}
              className="relative flex min-w-0 items-center gap-2 rounded-xl px-1.5 py-2"
            >
              <button
                type="button"
                className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full"
                onClick={() => changeCurrentConversation(member)}
              >
                <LoadingSkeletonImageDynamic
                  radius="9999px"
                  className="h-10 w-10 rounded-full object-cover"
                  image_height="2.5rem"
                  image_url={member.User.profile_pic}
                  image_width="2.5rem"
                />
                {OnlineUserList.onlineUsers.find(
                  (user) => user.user_id == member.User.user_id,
                ) && (
                  <img
                    className="absolute bottom-0 right-0 z-30 h-3.5 w-3.5"
                    src="/Home/Online_Green_dot.png"
                    alt=""
                  />
                )}
              </button>

              <button
                type="button"
                onClick={() => changeCurrentConversation(member)}
                className="min-w-0 flex-1 cursor-pointer text-left"
              >
                <div className="truncate text-sm font-medium capitalize text-darkText">
                  {member.User.user_id == userData.user_id ? (
                    "You"
                  ) : (
                    <ShowSavedName
                      user_id={member.User.user_id.toString()}
                      name={member.User.full_name}
                    />
                  )}
                </div>
                <div className="truncate text-sm text-[#959595]">
                  {member.User.user_name || "Available"}
                </div>
              </button>

              <div className="flex shrink-0 items-center gap-1">
                {member.is_admin && (
                  <div
                    className="whitespace-nowrap rounded-lg px-2 py-1 text-[11px] font-medium"
                    style={{
                      backgroundColor:
                        "color-mix(in srgb, var(--theme-color) 15%, transparent)",
                      color: "var(--theme-text-color)",
                    }}
                  >
                    <TextTranslate text="Group Admin" />
                  </div>
                )}

                {isCurrentUserAdmin &&
                  member.User.user_id != userData.user_id && (
                    <Menu>
                      <MenuButton className="inline-flex items-center rounded-md p-1 text-darkText/70 focus:outline-none data-[focus]:bg-black/[0.06] dark:data-[focus]:bg-white/10">
                        <PiDotsThreeVerticalBold
                          onClick={() => {
                            dispatch(updateSelectedGroupMember(member));
                          }}
                          className="cursor-pointer text-2xl"
                        />
                      </MenuButton>

                      <MenuItems
                        transition
                        anchor="bottom end"
                        className="z-[60] w-52 origin-top-right rounded-xl border border-borderColor bg-modalBg p-1 text-sm transition duration-200 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
                      >
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              type="button"
                              onClick={() => makeGrouAdmin()}
                              className={`group flex w-full items-center gap-2 rounded-lg py-1.5 pl-4 pr-3 text-left ${
                                focus ? "bg-dropdownOptionHover" : ""
                              }`}
                            >
                              {member.is_admin
                                ? "Remove From Admin"
                                : "Make Group Admin"}
                            </button>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              type="button"
                              onClick={() =>
                                dispatch(
                                  updateMessageOptions({
                                    showModal: true,
                                    title: `Are you sure you want Remove ${selectedGroupMember.User.full_name} From this Group??`,
                                    description: "",
                                    modalName: "remove_member",
                                  }),
                                )
                              }
                              className={`group flex w-full items-center gap-2 rounded-lg py-1.5 pl-4 pr-3 text-left ${
                                focus ? "bg-dropdownOptionHover" : ""
                              }`}
                            >
                              Remove
                            </button>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  )}
              </div>
            </div>
          </ContextMenuTrigger>
        ))
      )}
    </div>
  );
}
